import type { View } from '../router/router';
import { router } from '../router/router';
import { apiClient } from '../services/api-client';
import { toast } from '../components/layout/toast';
import { $ } from '../utils/dom';
import { escapeHtml } from '../utils/catalog-labels';
import {
  categoryLabel,
  MAX_TEMPLATE_EXERCISES,
  TEMPLATE_CATEGORIES,
  type TemplateCategory,
} from '../utils/template-labels';
import { getTyrisIcon } from '../components/ui/tyris-icon';

interface CatalogExercise {
  id: string;
  name: string;
  restSec: number;
}

interface TemplateLine {
  exerciseId: string;
  sets: number;
  reps: string;
  weight?: number | null;
  rpe?: number | null;
  rir?: number | null;
  restSec?: number | null;
  order: number;
  exercise?: CatalogExercise & { machine?: { name: string; code: string } | null };
}

interface TemplateDetail {
  id: string;
  name: string;
  category: TemplateCategory;
  description?: string | null;
  updatedAt: string;
  exercises: TemplateLine[];
  _count?: { appliedRoutines: number };
}

interface Student {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

type PageMode = 'view' | 'create' | 'edit' | 'apply';

export class AdminRoutineTemplatePage implements View {
  private mode: PageMode = 'view';
  private templateId = '';
  private template: TemplateDetail | null = null;
  private catalog: CatalogExercise[] = [];
  private students: Student[] = [];
  private lines: TemplateLine[] = [];
  private loading = true;
  private errorMsg: string | null = null;

  async render(): Promise<string> {
    this.readParams();
    await this.loadData();

    if (this.loading) {
      return `<div class="admin-state-loading" data-testid="admin-template-loading"><p>Cargando...</p></div>`;
    }

    if (this.errorMsg) {
      return `<div class="card-glass admin-state-error"><p>${escapeHtml(this.errorMsg)}</p></div>`;
    }

    if (this.mode === 'apply' && this.template) return this.renderApply();
    if (this.mode === 'view' && this.template) return this.renderView();
    if (this.mode === 'create' || this.mode === 'edit') return this.renderForm();
    return `<div class="admin-empty"><p>Template no encontrada.</p></div>`;
  }

  afterRender(): void {
    if (this.mode === 'view') {
      $('#btn-delete-template')?.addEventListener('click', () => this.handleDelete());
      return;
    }
    if (this.mode === 'apply') {
      $('#form-apply-template')?.addEventListener('submit', (e) => this.handleApply(e));
      $('#btn-cancel-apply')?.addEventListener('click', () => {
        window.location.hash = `#/admin/routine-template?id=${this.templateId}`;
      });
      return;
    }

    $('#btn-cancel-template-form')?.addEventListener('click', () => {
      window.location.hash = this.templateId
        ? `#/admin/routine-template?id=${this.templateId}`
        : '#/admin/routine-templates';
    });
    $('#btn-add-template-line')?.addEventListener('click', () => this.addLine());
    $('#form-template')?.addEventListener('submit', (e) => this.handleSubmit(e));
    this.bindLineControls();
  }

  private renderView(): string {
    const t = this.template!;
    const sorted = [...t.exercises].sort((a, b) => a.order - b.order);

    return `
      <div class="dashboard-header">
        <div class="dashboard-title">
          <a href="#/admin/routine-templates" class="admin-back-link">← Templates</a>
          <h2 data-testid="admin-template-name">${escapeHtml(t.name)}</h2>
          <p><span class="badge badge-admin">${categoryLabel(t.category)}</span></p>
        </div>
      </div>

      <div class="admin-detail-grid">
        <section class="card-glass">
          <h3 class="admin-section-title">Descripción</h3>
          <p>${escapeHtml(t.description ?? 'Sin descripción')}</p>
          <p class="admin-cell-muted" style="margin-top:0.75rem;">Actualizada: ${new Date(t.updatedAt).toLocaleDateString('es-ES')}</p>
          <p class="admin-cell-muted">Rutinas generadas desde esta base: ${t._count?.appliedRoutines ?? 0}</p>
        </section>
        <section class="card-glass">
          <h3 class="admin-section-title">Acciones</h3>
          <div class="admin-quick-links">
            <a href="#/admin/routine-template?form=edit&id=${t.id}" class="btn btn-primary" data-testid="btn-edit-template" style="display:inline-flex;align-items:center;gap:0.4rem;">${getTyrisIcon('editar', { size: 16 })} Editar</a>
            <a href="#/admin/routine-template?form=apply&id=${t.id}" class="btn btn-primary" data-testid="btn-apply-template" style="display:inline-flex;align-items:center;gap:0.4rem;">${getTyrisIcon('mis-rutinas', { size: 16 })} Aplicar a alumnos</a>
            <button type="button" class="btn btn-outline" id="btn-delete-template" data-testid="btn-delete-template"
              style="border-color:rgba(220,53,69,0.35);color:hsl(350,80%,65%);display:inline-flex;align-items:center;gap:0.4rem;">${getTyrisIcon('darse-de-baja', { size: 16 })} Eliminar</button>
          </div>
        </section>
      </div>

      <section class="card-glass admin-table-wrap" style="margin-top:1.5rem;" data-testid="admin-template-preview">
        <h3 class="admin-section-title">Preview de ejercicios (${sorted.length})</h3>
        <table class="admin-table">
          <thead>
            <tr><th>#</th><th>Ejercicio</th><th>Series</th><th>Reps</th><th>Peso</th><th>RPE</th><th>RIR</th><th>Descanso (s)</th><th>Máquina</th></tr>
          </thead>
          <tbody>
            ${sorted
              .map(
                (line, idx) => `
              <tr>
                <td>${idx + 1}</td>
                <td><strong>${escapeHtml(line.exercise?.name ?? '—')}</strong></td>
                <td>${line.sets}</td>
                <td>${escapeHtml(line.reps)}</td>
                <td>${line.weight != null ? `${line.weight} kg` : '—'}</td>
                <td>${line.rpe ?? '—'}</td>
                <td>${line.rir ?? '—'}</td>
                <td>${this.renderEffectiveRest(line)}</td>
                <td class="admin-cell-muted">${line.exercise?.machine ? escapeHtml(line.exercise.machine.name) : '—'}</td>
              </tr>
            `,
              )
              .join('')}
          </tbody>
        </table>
      </section>
    `;
  }

  private renderApply(): string {
    const t = this.template!;
    const sorted = [...t.exercises].sort((a, b) => a.order - b.order);

    return `
      <div class="dashboard-header">
        <div class="dashboard-title">
          <a href="#/admin/routine-template?id=${t.id}" class="admin-back-link">← ${escapeHtml(t.name)}</a>
          <h2 data-testid="admin-template-apply-title">Aplicar template</h2>
          <p>Genera rutinas independientes por alumno. La template base no se modifica.</p>
        </div>
      </div>

      <div class="card-glass" style="margin-bottom:1.5rem;" data-testid="apply-template-preview">
        <h3 class="admin-section-title">Preview (${sorted.length} ejercicios)</h3>
        <ul class="admin-mini-list">
          ${sorted
            .map(
              (line, idx) => `
            <li><strong>${idx + 1}. ${escapeHtml(line.exercise?.name ?? 'Ejercicio')}</strong> — ${line.sets}×${escapeHtml(line.reps)} · Descanso: ${this.renderEffectiveRest(line)}</li>
          `,
            )
            .join('')}
        </ul>
      </div>

      <form id="form-apply-template" class="card-glass admin-form-card" data-testid="form-apply-template">
        <h3 class="admin-section-title">Seleccionar alumnos</h3>
        ${this.renderStudentChecklist()}
        <label class="tyris-check admin-apply-active">
          <span class="tyris-check-control">
            <input type="checkbox" name="isActive" checked class="tyris-check-input" data-testid="apply-is-active" />
            <span class="tyris-check-box" aria-hidden="true">${getTyrisIcon('check', { size: 12 })}</span>
          </span>
          <span class="tyris-check-text">Marcar como rutina activa de cada alumno</span>
        </label>
        <div class="admin-form-actions">
          <button type="submit" class="btn btn-primary" data-testid="apply-template-submit">Aplicar template</button>
          <button type="button" class="btn btn-outline" id="btn-cancel-apply">Cancelar</button>
        </div>
      </form>
    `;
  }

  private renderStudentChecklist(): string {
    const students = [...this.students].sort(
      (a, b) =>
        a.lastName.localeCompare(b.lastName, 'es', { sensitivity: 'base' }) ||
        a.firstName.localeCompare(b.firstName, 'es', { sensitivity: 'base' }) ||
        a.email.localeCompare(b.email, 'es', { sensitivity: 'base' }),
    );

    const counter = `
      <p class="admin-student-checklist-count" data-testid="apply-student-count">
        ${getTyrisIcon('users', { size: 14 })}
        ${students.length} alumno${students.length === 1 ? '' : 's'} · orden alfabético por apellidos y nombre
      </p>
    `;

    if (students.length === 0) {
      return `${counter}<p class="admin-empty">No hay alumnos activos disponibles.</p>`;
    }

    return `
      ${counter}
      <ul class="admin-student-checklist" data-testid="apply-student-list">
        ${students
          .map(
            (s) => `
          <li class="admin-student-checklist-item">
            <label class="tyris-check admin-student-check" title="${escapeHtml(`${s.firstName} ${s.lastName} — ${s.email}`)}">
              <span class="tyris-check-control">
                <input type="checkbox" name="studentIds" value="${s.id}" class="tyris-check-input" data-testid="apply-student-${s.id}" />
                <span class="tyris-check-box" aria-hidden="true">${getTyrisIcon('check', { size: 12 })}</span>
              </span>
              <span class="admin-student-icon" aria-hidden="true">${getTyrisIcon('perfil', { size: 18 })}</span>
              <span class="admin-student-meta">
                <strong>${escapeHtml(s.firstName)} ${escapeHtml(s.lastName)}</strong>
                <small>${escapeHtml(s.email)}</small>
              </span>
            </label>
          </li>
        `,
          )
          .join('')}
      </ul>
    `;
  }

  private renderForm(): string {
    const t = this.template;
    const title = this.mode === 'create' ? 'Nueva template' : 'Editar template';

    return `
      <div class="dashboard-header">
        <div class="dashboard-title">
          <h2 data-testid="admin-template-form-title">${title}</h2>
          <p>Hasta ${MAX_TEMPLATE_EXERCISES} ejercicios. Puedes repetir el mismo ejercicio.</p>
        </div>
      </div>

      <form id="form-template" class="card-glass admin-form-card" data-testid="admin-template-form">
        <div class="admin-form-grid">
          <label>Nombre *
            <input name="name" class="admin-input" required value="${escapeHtml(t?.name ?? '')}" data-testid="template-form-name" />
          </label>
          <label>Categoría / split *
            <select name="category" class="admin-select" required data-testid="template-form-category">
              ${TEMPLATE_CATEGORIES.map(
                (c) => `
                <option value="${c.value}" ${t?.category === c.value ? 'selected' : ''}>${c.label}</option>
              `,
              ).join('')}
            </select>
          </label>
        </div>
        <label style="display:block;margin-top:1rem;">Descripción
          <textarea name="description" class="admin-input admin-textarea" rows="2" data-testid="template-form-description">${escapeHtml(t?.description ?? '')}</textarea>
        </label>

        <div style="margin-top:1.5rem;">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:0.75rem;">
            <h3 class="admin-section-title" style="margin:0;">Ejercicios (${this.lines.length}/${MAX_TEMPLATE_EXERCISES})</h3>
            <button type="button" class="btn btn-outline btn-sm" id="btn-add-template-line" data-testid="btn-add-template-line"
              ${this.lines.length >= MAX_TEMPLATE_EXERCISES ? 'disabled' : ''} style="display:inline-flex;align-items:center;gap:0.25rem;">${getTyrisIcon('plus', { size: 14 })} Agregar</button>
          </div>
          <div id="template-lines-container">${this.lines.length === 0 ? '<p class="admin-cell-muted">Agrega ejercicios del catálogo.</p>' : this.renderLinesEditor()}</div>
        </div>

        <div class="admin-form-actions">
          <button type="submit" class="btn btn-primary" data-testid="template-form-submit">Guardar template</button>
          <button type="button" class="btn btn-outline" id="btn-cancel-template-form">Cancelar</button>
        </div>
      </form>
    `;
  }

  private getExerciseDefaultRest(exerciseId: string): number {
    return this.catalog.find((ex) => ex.id === exerciseId)?.restSec ?? 120;
  }

  private renderEffectiveRest(line: TemplateLine): string {
    if (line.restSec != null) return `${line.restSec} s`;
    const fallback = line.exercise?.restSec ?? this.getExerciseDefaultRest(line.exerciseId);
    return `${fallback} s (auto)`;
  }

  private renderLinesEditor(): string {
    return `
      <table class="admin-table routine-builder-table">
        <thead><tr><th>Orden</th><th>Ejercicio</th><th>Series</th><th>Reps</th><th>Peso</th><th>RPE</th><th>RIR</th><th>Descanso (s)</th><th></th></tr></thead>
        <tbody id="template-lines-body">
          ${this.lines.map((line, idx) => this.renderLineRow(line, idx)).join('')}
        </tbody>
      </table>
    `;
  }

  private renderLineRow(line: TemplateLine, idx: number): string {
    return `
      <tr data-line-index="${idx}">
        <td>
          <span class="routine-order-badge">${idx + 1}</span>
          <div class="admin-row-actions" style="margin-top:0.25rem;">
            <button type="button" class="btn btn-outline btn-sm" data-move-up="${idx}" ${idx === 0 ? 'disabled' : ''}>↑</button>
            <button type="button" class="btn btn-outline btn-sm" data-move-down="${idx}" ${idx === this.lines.length - 1 ? 'disabled' : ''}>↓</button>
          </div>
        </td>
        <td>
          <select class="admin-select line-exercise-id" required data-testid="template-line-exercise-${idx}">
            ${this.catalog.map((ex) => `<option value="${ex.id}" ${line.exerciseId === ex.id ? 'selected' : ''}>${escapeHtml(ex.name)}</option>`).join('')}
          </select>
        </td>
        <td><input type="number" class="admin-input line-sets" min="1" value="${line.sets}" style="width:4rem;" /></td>
        <td>
          <input type="text" class="admin-input line-reps" value="${escapeHtml(line.reps)}" style="width:6rem;" placeholder="ej: 8-12" />
          <div style="font-size:0.7rem;color:var(--text-muted);margin-top:0.2rem;">ej: 10, 8-12, fallo</div>
        </td>
        <td><input type="number" class="admin-input line-weight" min="0" step="0.5" value="${line.weight ?? ''}" style="width:5rem;" /></td>
        <td><input type="number" class="admin-input line-rpe" min="1" max="10" value="${line.rpe ?? ''}" style="width:3.5rem;" /></td>
        <td><input type="number" class="admin-input line-rir" min="0" max="10" value="${line.rir ?? ''}" style="width:3.5rem;" title="RIR: 0=fallo, 1-4=zona de trabajo" /></td>
        <td><input type="number" class="admin-input line-rest" min="0" max="600" step="5"
          value="${line.restSec ?? ''}" placeholder="Auto (${this.getExerciseDefaultRest(line.exerciseId)} s)"
          style="width:7rem;" title="Vacío = hereda el descanso del ejercicio"
          data-testid="template-line-rest-${idx}" /></td>
        <td><button type="button" class="btn btn-outline btn-sm" data-remove-line="${idx}" style="display:inline-flex;align-items:center;">${getTyrisIcon('close', { size: 16 })}</button></td>
      </tr>
    `;
  }

  private readParams(): void {
    const params = router.getParams();
    this.templateId = params.id ?? '';
    if (params.form === 'create') this.mode = 'create';
    else if (params.form === 'edit' && params.id) {
      this.mode = 'edit';
      this.templateId = params.id;
    } else if (params.form === 'apply' && params.id) {
      this.mode = 'apply';
      this.templateId = params.id;
    } else if (params.id) this.mode = 'view';
  }

  private async loadData(): Promise<void> {
    try {
      this.catalog = await apiClient.get<CatalogExercise[]>('/api/exercises');

      if (this.mode === 'view' || this.mode === 'edit' || this.mode === 'apply') {
        if (!this.templateId) {
          this.errorMsg = 'ID no especificado';
          return;
        }
        this.template = await apiClient.get<TemplateDetail>(`/api/routine-templates/${this.templateId}`);
        if (this.mode === 'edit') {
          this.lines = this.template.exercises
            .map((ex) => ({
              exerciseId: ex.exerciseId,
              sets: ex.sets,
              reps: ex.reps,
              weight: ex.weight,
              rpe: ex.rpe,
              rir: ex.rir,
              restSec: ex.restSec,
              order: ex.order,
            }))
            .sort((a, b) => a.order - b.order);
        }
        if (this.mode === 'apply') {
          this.students = await apiClient.get<Student[]>('/api/users/students');
        }
      } else if (this.mode === 'create') {
        const defaultEx = this.catalog[0];
        this.lines = defaultEx
          ? [{ exerciseId: defaultEx.id, sets: 3, reps: '8-12', order: 1 }]
          : [];
      }
    } catch (err: unknown) {
      this.errorMsg = err instanceof Error ? err.message : 'Error al cargar';
    } finally {
      this.loading = false;
    }
  }

  private syncLinesFromDom(): void {
    const rows = document.querySelectorAll('#template-lines-body tr');
    rows.forEach((row, idx) => {
      const line = this.lines[idx];
      if (!line) return;
      const exerciseSelect = row.querySelector('.line-exercise-id') as HTMLSelectElement;
      const setsInput = row.querySelector('.line-sets') as HTMLInputElement;
      const repsInput = row.querySelector('.line-reps') as HTMLInputElement;
      const weightInput = row.querySelector('.line-weight') as HTMLInputElement;
      const rpeInput = row.querySelector('.line-rpe') as HTMLInputElement;
      if (exerciseSelect) line.exerciseId = exerciseSelect.value;
      if (setsInput) line.sets = Number(setsInput.value) || 1;
      if (repsInput) line.reps = repsInput.value;
      line.weight = weightInput?.value !== '' ? Number(weightInput.value) : null;
      line.rpe = rpeInput?.value !== '' ? Number(rpeInput.value) : null;
      const rirInput = row.querySelector('.line-rir') as HTMLInputElement;
      line.rir = rirInput?.value !== '' ? Number(rirInput.value) : null;
      const restInput = row.querySelector('.line-rest') as HTMLInputElement;
      line.restSec = restInput?.value !== '' ? Number(restInput.value) : null;
      line.order = idx + 1;
    });
  }

  private bindLineControls(): void {
    document.querySelectorAll('[data-move-up]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.moveLine(Number((btn as HTMLElement).getAttribute('data-move-up')), -1);
      });
    });
    document.querySelectorAll('[data-move-down]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.moveLine(Number((btn as HTMLElement).getAttribute('data-move-down')), 1);
      });
    });
    document.querySelectorAll('[data-remove-line]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.removeLine(Number((btn as HTMLElement).getAttribute('data-remove-line')));
      });
    });
    document.querySelectorAll('.line-exercise-id').forEach((select) => {
      select.addEventListener('change', () => {
        const row = select.closest('tr');
        const restInput = row?.querySelector('.line-rest') as HTMLInputElement | null;
        if (restInput) {
          restInput.placeholder = `Auto (${this.getExerciseDefaultRest(
            (select as HTMLSelectElement).value,
          )} s)`;
        }
      });
    });
  }

  private refreshLinesDom(): void {
    const container = $('#template-lines-container');
    if (!container) return;
    container.innerHTML =
      this.lines.length === 0
        ? '<p class="admin-cell-muted">Agrega ejercicios del catálogo.</p>'
        : this.renderLinesEditor();
    const addBtn = $('#btn-add-template-line');
    if (addBtn) addBtn.toggleAttribute('disabled', this.lines.length >= MAX_TEMPLATE_EXERCISES);
    this.bindLineControls();
  }

  private addLine(): void {
    if (this.lines.length >= MAX_TEMPLATE_EXERCISES) {
      toast.error(`Máximo ${MAX_TEMPLATE_EXERCISES} ejercicios por template.`);
      return;
    }
    this.syncLinesFromDom();
    const ex = this.catalog[0];
    if (!ex) {
      toast.error('No hay ejercicios en el catálogo.');
      return;
    }
    this.lines.push({ exerciseId: ex.id, sets: 3, reps: '8-12', order: this.lines.length + 1 });
    this.refreshLinesDom();
  }

  private removeLine(idx: number): void {
    this.syncLinesFromDom();
    this.lines.splice(idx, 1);
    this.lines.forEach((l, i) => {
      l.order = i + 1;
    });
    this.refreshLinesDom();
  }

  private moveLine(idx: number, delta: number): void {
    this.syncLinesFromDom();
    const target = idx + delta;
    if (target < 0 || target >= this.lines.length) return;
    [this.lines[idx], this.lines[target]] = [this.lines[target], this.lines[idx]];
    this.lines.forEach((l, i) => {
      l.order = i + 1;
    });
    this.refreshLinesDom();
  }

  private collectFormPayload(form: HTMLFormElement) {
    this.syncLinesFromDom();
    const fd = new FormData(form);
    const name = String(fd.get('name') ?? '').trim();
    const category = String(fd.get('category')) as TemplateCategory;
    const description = String(fd.get('description') ?? '').trim();

    if (!name || this.lines.length === 0) {
      toast.error('Nombre y al menos un ejercicio son obligatorios.');
      return null;
    }

    return {
      name,
      category,
      description: description || undefined,
      exercises: this.lines.map((l, i) => ({
        exerciseId: l.exerciseId,
        sets: l.sets,
        reps: l.reps.trim(),
        weight: l.weight ?? undefined,
        rpe: l.rpe ?? undefined,
        rir: l.rir ?? undefined,
        ...(l.restSec != null ? { restSec: l.restSec } : {}),
        order: i + 1,
      })),
    };
  }

  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();
    const payload = this.collectFormPayload(e.target as HTMLFormElement);
    if (!payload) return;

    try {
      if (this.mode === 'create') {
        const created = await apiClient.post<{ id: string }>('/api/routine-templates', payload);
        toast.success('Template creada.');
        window.location.hash = `#/admin/routine-template?id=${created.id}`;
      } else if (this.mode === 'edit' && this.templateId) {
        await apiClient.patch(`/api/routine-templates/${this.templateId}`, payload);
        toast.success('Template actualizada.');
        window.location.hash = `#/admin/routine-template?id=${this.templateId}`;
      }
    } catch (err: unknown) {
      const body = (err as { body?: { message?: string | string[] } })?.body;
      const msg = Array.isArray(body?.message) ? body.message.join(', ') : body?.message;
      toast.error(msg ?? 'No se pudo guardar.');
    }
  }

  private async handleApply(e: Event): Promise<void> {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const fd = new FormData(form);
    const studentIds = fd.getAll('studentIds').map(String);
    const isActive = fd.get('isActive') === 'on';

    if (studentIds.length === 0) {
      toast.error('Selecciona al menos un alumno.');
      return;
    }

    const msg =
      studentIds.length > 1
        ? `¿Aplicar "${this.template?.name}" a ${studentIds.length} alumnos? Se crearán rutinas independientes.`
        : `¿Aplicar "${this.template?.name}" al alumno seleccionado?`;
    if (!confirm(msg)) return;

    try {
      const result = await apiClient.post<{ appliedCount: number }>(
        `/api/routine-templates/${this.templateId}/apply`,
        { studentIds, isActive },
      );
      toast.success(`Template aplicada a ${result.appliedCount} alumno(s).`);
      window.location.hash = '#/admin/routine-templates';
    } catch (err: unknown) {
      const body = (err as { body?: { message?: string | string[] } })?.body;
      const msg = Array.isArray(body?.message) ? body.message.join(', ') : body?.message;
      toast.error(msg ?? 'No se pudo aplicar.');
    }
  }

  private async handleDelete(): Promise<void> {
    if (!this.templateId || !this.template) return;
    if (!confirm(`¿Eliminar la template "${this.template.name}"? No afecta rutinas ya aplicadas.`)) return;
    try {
      await apiClient.delete(`/api/routine-templates/${this.templateId}`);
      toast.success('Template eliminada.');
      window.location.hash = '#/admin/routine-templates';
    } catch (err: unknown) {
      const body = (err as { body?: { message?: string } })?.body;
      toast.error(body?.message ?? 'No se pudo eliminar.');
    }
  }
}
